import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wand2, Loader2, Package, FolderTree, Languages } from 'lucide-react';
import { translationsApi } from '../../api/endpoints/translations';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { ProgressBar } from '../../components/ui/ProgressBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatCard } from '../../components/ui/StatCard';
import { useToast } from '../../components/ui/Alert';
import { LANGUAGES, getLanguageLabel } from '../../lib/constants';
import type { TranslationEntry, SaveTranslationInput } from '../../types/translation';

export function TranslationsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [targetLang, setTargetLang] = useState('en');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [translatingEntityIds, setTranslatingEntityIds] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['translations', storeId],
    queryFn: () => translationsApi.list(storeId!),
    enabled: !!storeId,
  });

  const saveMutation = useMutation({
    mutationFn: (input: SaveTranslationInput) => translationsApi.save(storeId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['translations', storeId] });
      toast('success', t('common.success'));
      setEdits({});
    },
    onError: () => toast('error', t('common.error')),
  });

  const bulkTranslateMutation = useMutation({
    mutationFn: (entityType: string) =>
      translationsApi.bulkTranslate(storeId!, {
        entityType,
        targetLanguage: targetLang,
        sourceLanguage: 'ar',
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['translations', storeId] });
      toast('success', t('translations.autoTranslateSuccess', { count: result.translated }));
    },
    onError: () => toast('error', t('common.error')),
  });

  const entityTranslateMutation = useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      translationsApi.autoTranslate(storeId!, {
        entityType,
        entityId,
        sourceLanguage: 'ar',
        targetLanguage: targetLang,
      }),
    onMutate: ({ entityId }) => {
      setTranslatingEntityIds((prev) => new Set(prev).add(entityId));
    },
    onSuccess: (result, { entityType, entityId }) => {
      // Apply translated values to the edits so the user sees them immediately
      const newEdits = { ...edits };
      for (const [field, value] of Object.entries(result.translations)) {
        const key = getEditKey(entityType, entityId, field);
        newEdits[key] = value;
      }
      setEdits(newEdits);
      queryClient.invalidateQueries({ queryKey: ['translations', storeId] });
      toast('success', t('translations.autoTranslateSuccess', { count: Object.keys(result.translations).length }));
    },
    onError: () => toast('error', t('common.error')),
    onSettled: (_data, _error, { entityId }) => {
      setTranslatingEntityIds((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
    },
  });

  const handleSave = () => {
    const translations = Object.entries(edits).map(([key, value]) => {
      const parts = key.split('::');
      return { entity: parts[0] || '', entityId: parts[1] || '', field: parts[2] || '', language: targetLang, value };
    });
    if (translations.length === 0) return;
    saveMutation.mutate({ translations });
  };

  const getEditKey = (entity: string, entityId: string, field: string) =>
    `${entity}::${entityId}::${field}`;

  const groups = data?.groups ?? [];

  // Compute stats from groups
  const translationStats = (() => {
    const productGroup = groups.find(g => g.entity === 'product' || g.entity === 'products');
    const categoryGroup = groups.find(g => g.entity === 'category' || g.entity === 'categories');

    const productCount = productGroup
      ? new Set(productGroup.entries.map(e => e.entityId)).size
      : 0;
    const categoryCount = categoryGroup
      ? new Set(categoryGroup.entries.map(e => e.entityId)).size
      : 0;

    // Count translated vs total entries for the selected target language
    let totalFields = 0;
    let translatedFields = 0;
    for (const group of groups) {
      for (const entry of group.entries) {
        totalFields++;
        const val = edits[getEditKey(entry.entity, entry.entityId, entry.field)] ?? entry.values[targetLang];
        if (val && val.trim().length > 0) translatedFields++;
      }
    }
    return { productCount, categoryCount, translatedFields, totalFields };
  })();

  if (isLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.translations')}</h1>
        <div className="flex items-center gap-3">
          <Select
            options={LANGUAGES.map((l) => ({ value: l, label: getLanguageLabel(l) }))}
            value={targetLang}
            onChange={(e) => setTargetLang(e.target.value)}
            className="w-40"
          />
          <Button
            variant="secondary"
            onClick={() => {
              const types = groups.map(g => g.entity);
              if (types.length > 0 && types[0]) bulkTranslateMutation.mutate(types[0]);
            }}
            isLoading={bulkTranslateMutation.isPending}
            disabled={groups.length === 0 || targetLang === 'ar'}
          >
            <Wand2 className="h-4 w-4" />
            {t('translations.autoTranslateAll', 'Auto-translate All')}
          </Button>
          <Button
            onClick={handleSave}
            isLoading={saveMutation.isPending}
            disabled={Object.keys(edits).length === 0}
          >
            {t('common.save')} {Object.keys(edits).length > 0 && `(${Object.keys(edits).length})`}
          </Button>
        </div>
      </div>

      {groups.length > 0 && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <StatCard
            title={t('translations.totalProducts', 'Products')}
            value={translationStats.productCount}
            icon={<Package className="h-5 w-5" />}
          />
          <StatCard
            title={t('translations.totalCategories', 'Categories')}
            value={translationStats.categoryCount}
            icon={<FolderTree className="h-5 w-5" />}
          />
          <StatCard
            title={t('translations.progress', 'Translation Progress')}
            value={`${translationStats.translatedFields}/${translationStats.totalFields}`}
            icon={<Languages className="h-5 w-5" />}
          />
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyState title={t('common.noData')} description={t('translations.empty')} />
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.entity} className="rounded-xl border border-border-default bg-bg-secondary p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold capitalize">{group.entity}</h2>
                <div className="flex items-center gap-3">
                  {targetLang !== 'ar' && (
                    <button
                      onClick={() => bulkTranslateMutation.mutate(group.entity)}
                      disabled={bulkTranslateMutation.isPending}
                      className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 transition-colors disabled:opacity-50"
                    >
                      <Wand2 className="h-3.5 w-3.5" />
                      {t('translations.autoTranslate', 'Auto-translate')}
                    </button>
                  )}
                  {group.progress[targetLang] != null && (
                    <ProgressBar
                      value={group.progress[targetLang]}
                      className="w-32"
                      variant={group.progress[targetLang] >= 100 ? 'success' : group.progress[targetLang] >= 50 ? 'warning' : 'danger'}
                    />
                  )}
                </div>
              </div>
              <div className="space-y-3">
                {(() => {
                  // Group entries by entityId to render per-entity headers with translate buttons
                  const entityGroups: { entityId: string; entries: TranslationEntry[] }[] = [];
                  const seen = new Map<string, number>();
                  for (const entry of group.entries) {
                    const idx = seen.get(entry.entityId);
                    if (idx != null && entityGroups[idx]) {
                      entityGroups[idx].entries.push(entry);
                    } else {
                      seen.set(entry.entityId, entityGroups.length);
                      entityGroups.push({ entityId: entry.entityId, entries: [entry] });
                    }
                  }

                  return entityGroups.map(({ entityId, entries }) => {
                    const isTranslating = translatingEntityIds.has(entityId);
                    // Use the source value of the first entry as the entity label
                    const firstValues = entries[0]?.values || {};
                    const rawLabel = firstValues['ar'] || firstValues[Object.keys(firstValues)[0] ?? ''] || entityId;
                    const entityLabel = typeof rawLabel === 'string' ? rawLabel : entityId;

                    return (
                      <div key={entityId} className="border-b border-border-default/30 last:border-0 pb-3 last:pb-0">
                        {/* Entity header with per-entity translate button */}
                        <div className="flex items-center justify-between mb-2 mt-1">
                          <p className="text-xs font-medium text-text-secondary truncate max-w-[70%]">{entityLabel}</p>
                          {targetLang !== 'ar' && (
                            <button
                              onClick={() => entityTranslateMutation.mutate({ entityType: group.entity, entityId })}
                              disabled={isTranslating}
                              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-text-muted hover:text-accent hover:bg-bg-hover transition-colors disabled:opacity-50"
                              title={t('translations.autoTranslateEntity', 'Auto-translate this item')}
                            >
                              {isTranslating ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Wand2 className="h-3 w-3" />
                              )}
                              {t('translations.translate', 'Translate')}
                            </button>
                          )}
                        </div>

                        {entries.map((entry) => {
                          const key = getEditKey(entry.entity, entry.entityId, entry.field);
                          const vals = entry.values || {};
                          const rawCurrent = edits[key] ?? vals[targetLang ?? ''] ?? '';
                          const rawSource = vals['ar'] || vals[Object.keys(vals)[0] ?? ''] || '';
                          // Values can be objects (e.g. nested variant translations) - stringify them
                          const currentValue = typeof rawCurrent === 'string' ? rawCurrent : JSON.stringify(rawCurrent);
                          const sourceValue = typeof rawSource === 'string' ? rawSource : JSON.stringify(rawSource);
                          const fieldLower = entry.field.toLowerCase();
                          const useTextarea =
                            fieldLower.includes('description') ||
                            fieldLower.includes('content') ||
                            fieldLower.includes('body') ||
                            sourceValue.length > 80;
                          return (
                            <div key={entry.key} className="grid gap-3 sm:grid-cols-2 py-2 border-b border-border-default/50 last:border-0">
                              <div>
                                <p className="text-xs text-text-muted mb-1">{entry.field}</p>
                                <p className="text-sm">{sourceValue}</p>
                              </div>
                              {useTextarea ? (
                                <Textarea
                                  value={currentValue}
                                  onChange={(e) => setEdits({ ...edits, [key]: e.target.value })}
                                  placeholder={t('translations.enterTranslation')}
                                  rows={3}
                                />
                              ) : (
                                <Input
                                  value={currentValue}
                                  onChange={(e) => setEdits({ ...edits, [key]: e.target.value })}
                                  placeholder={t('translations.enterTranslation')}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
